import {
  ColumnDefinition,
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiDataSearchComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiDataTableComponent,
  OuiDialogComponent,
  OuiDialogHeaderComponent,
  OuiFileDropZoneComponent,
  OuiGridComponent,
  OuiIconButtonComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
  OuiTooltipComponent,
} from '@aiz-gl/omni-ui';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../../components/input/input.component';
import { SelectComponent } from '../../components/select/select.component';

import { PageComponent } from '../../components/page/page';
import {
  BuyerAccount,
  BuyerAccountUpdateResponse,
  SyncResponse,
} from '../../models/buyer-account.model';
import { Option } from '../../models/common.models';
import { BuyerAccountService } from '../../services/buyer-account/buyer-account.service';
import { BasePageComponent } from '../base/base.component';

@Component({
  selector: 'app-buyer-login',
  standalone: true,
  imports: [
    CommonModule,
    OuiCenterComponent,
    OuiDialogComponent,
    OuiDialogHeaderComponent,
    OuiBoxComponent,
    OuiDataSearchComponent,
    OuiDataTableComponent,
    OuiRowComponent,
    OuiTextComponent,
    OuiButtonComponent,
    InputComponent,
    SelectComponent,
    OuiGridComponent,
    OuiTooltipComponent,
    OuiIconButtonComponent,
    OuiFileDropZoneComponent,
    SelectComponent,
    OuiStackComponent,
    PageComponent,
  ],
  templateUrl: './buyer-management.component.html',
})
export class BuyerManagementPage extends BasePageComponent {
  private notifications = inject(NotificationService);
  private buyerLogin = inject(BuyerAccountService);

  formBuilder = inject(FormBuilder);

  buyerForm: FormGroup = this.formBuilder.group({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    role: new FormControl('', [Validators.required]),
    buyerCompanyNumber: new FormControl('', [Validators.required]),
  });

  buyers!: OuiDatasource<BuyerAccount>;
  saving = signal<boolean>(false);
  showDialog = signal<boolean>(false);
  dialogError = signal<string | undefined>(undefined);
  showRedFlagBuyerDialog = signal<boolean>(false);
  selectedBuyer = signal<BuyerAccount | undefined>(undefined);
  permissionToUpload = this.permissionTo('buyer:manage');
  selectedFile = signal<File | undefined>(undefined);
  fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');
  private readonly notification = inject(NotificationService);

  buyerColumns: ColumnDefinition<BuyerAccount>[] = [
    {
      columnDef: 'fullName',
      header: 'Full Name',
    },
    {
      columnDef: 'email',
      header: 'Email',
    },
    {
      columnDef: 'roles',
      header: 'Role',
      cell: (buyer) => buyer.roles,
    },
    {
      columnDef: 'buyerCompanyNumber',
      header: 'Buyer Company Number',
      cell: (buyer) => buyer.buyerCompanyNumber,
    },
  ];
  constructor() {
    super();

    // Initialize buyers from the service
    effect(() => {
      const buyers = this.buyerLogin.buyers.value();
      if (buyers) this.buyers = new OuiDatasource(buyers);
    });

    // Disable email input when editing a buyer
    effect(() => {
      if (this.selectedBuyer()) {
        this.buyerForm.controls['email'].disable();
      } else {
        this.buyerForm.controls['email'].enable();
      }
    });
  }

  roleOptions = computed(() => {
    const options: Option[] = [
      { label: 'Buyer Admin', value: 'BuyerAdmin' },
      { label: 'Invoice Admin', value: 'InvoiceAdmin' },
    ];
    return options;
  });

  confirmDelete = () =>
    this.notifications.showConfirmation({
      title: 'Delete Record',
      message: 'Are you sure you want to delete this buyer?',
      action: () => this.deleteSelectedBuyer(),
      actionLabel: 'Delete',
      dismissLabel: 'Cancel',
      color: 'secondary',
    });

  confirmReactivate = (updatedBuyer: BuyerAccount) =>
    this.notifications.showConfirmation({
      title: 'Reactivate Buyer Login',
      message: `A user with the email "${updatedBuyer.email}" already exists but is currently inactive. Would you like to reactivate this user?`,
      action: () => this.updatedBuyerLogin(updatedBuyer),
      actionLabel: 'Reactivate',
      dismissLabel: 'Cancel',
      color: 'secondary',
    });

  handleRowClick(buyer: BuyerAccount) {
    this.selectedBuyer.set(buyer);

    this.buyerForm.setValue({
      firstName: buyer.firstName,
      lastName: buyer.lastName,
      email: buyer.email,
      role: buyer.roles,
      buyerCompanyNumber: buyer.buyerCompanyNumber,
    });

    this.showDialog.set(true);
  }

  saveBuyer() {
    this.saving.set(true);
    const buyer = this.selectedBuyer();
    const updates = this.buyerForm.value;
    const updatedBuyer = {
      ...buyer,
      ...updates,
      roles: updates.role,
    };

    if (buyer) {
      this.updatedBuyerLogin(updatedBuyer as BuyerAccount);
    } else {
      const inactiveBuyers = this.buyerLogin.inactiveBuyers.value();
      const inactiveBuyerEmail = (inactiveBuyers ?? []).find(
        (b) => b.email === updatedBuyer.email,
      );
      if (inactiveBuyerEmail != undefined) {
        this.confirmReactivate(inactiveBuyerEmail);
        this.saving.set(false);
      } else {
        this.buyerLogin
          .createBuyer({ ...updatedBuyer, activeFlag: true })
          .subscribe({
            next: () => {
              this.buyerLogin.buyers.reload();
              this.notifications.showSnackbar({
                message: 'Buyer Created Successfully',
                variant: 'success',
                symbolName: 'account_circle',
              });
            },
            error: (response: HttpErrorResponse) => {
              this.handleSaveBuyerErrorResponse(response, updatedBuyer);
            },
            complete: () => {
              this.saving.set(false);
              this.showDialog.set(false);
            },
          });
      }
    }
  }

  handleSaveBuyerErrorResponse(
    response: HttpErrorResponse,
    inactiveBuyer: BuyerAccount,
  ) {
    const { status, error } = response;
    this.saving.set(false);
    if ([422].includes(status)) {
      this.dialogError.set(error);
    } else if ([409].includes(status)) {
      this.confirmReactivate(inactiveBuyer);
      this.saving.set(false);
    } else {
      this.showDialog.set(false);
    }
  }

  updatedBuyerLogin(updatedBuyer: BuyerAccount) {
    this.buyerLogin.saveBuyer(updatedBuyer).subscribe({
      next: () => {
        this.buyerLogin.buyers.reload();
        this.notifications.showSnackbar({
          message: 'Buyer Updated Successfully',
          variant: 'success',
          symbolName: 'account_circle',
        });
      },
      error: (response: HttpErrorResponse) => {
        this.handleSaveBuyerErrorResponse(response, updatedBuyer);
      },
      complete: () => {
        this.selectedBuyer.set(undefined);
        this.saving.set(false);
        this.showDialog.set(false);
      },
    });
  }

  deleteSelectedBuyer() {
    const buyer = this.selectedBuyer();
    if (!buyer) return;
    this.buyerLogin.deleteBuyerLogin(buyer).subscribe({
      next: (response: BuyerAccountUpdateResponse) => {
        this.showDialog.set(false);
        this.selectedBuyer.set(undefined);
        this.buyerLogin.buyers.reload();

        this.notifications.showSnackbar({
          message: response.success
            ? `buyer ${buyer.email} Deleted Successfully`
            : `Failed to delete buyer ${buyer.email}`,
          variant: 'warn',
          symbolName: 'account_circle_off',
        });
      },
      error: () => {
        this.showDialog.set(false);
      },
    });
  }

  addBuyer() {
    this.showDialog.set(true);
  }

  addRedFlagBuyer() {
    this.showRedFlagBuyerDialog.set(true);
    this.notification.hideAlert();
  }

  handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  uploadFile(file?: File) {
    if (!file) return;
    this.buyerLogin.uploadRedFlagBuyers(file).subscribe({
      next: () => {
        this.fileDropZone()?.selectedFile.set(undefined);
        this.selectedFile.set(undefined);
        this.showRedFlagBuyerDialog.set(false);
      },
      error: (err) => {
        const errorMessage =
          err.error || 'Failed to upload the file. Please try again.';
        this.notification.showAlert({
          message: errorMessage,
          variant: 'warn',
          symbolName: 'error',
        });
      },
    });
  }

  synchronizeBuyerLoginData() {
    this.notification.hideAlert();
    this.buyerLogin.syncBuyerLogin('atb').subscribe({
      next: (response: SyncResponse) => {
        if (response.results?.length > 0) {
          if (response.results.every((result) => result.success)) {
            this.notification.showSnackbar({
              message: `Sync completed successfully.`,
              variant: 'success',
              symbolName: 'check_circle',
            });
            return;
          }
          if (response.results.some((result) => !result.success)) {
            const errorMessages = response.results
              .map(
                (result) =>
                  result.error ||
                  'Failed to sync product. Please try again later.',
              )
              .join(', ');
            this.notification.showSnackbar({
              message: errorMessages,
              variant: 'warn',
              symbolName: 'error',
            });
            return;
          }
        } else {
          this.notification.showSnackbar({
            message: Array.isArray(response.errors)
              ? response.errors.join(', ')
              : response.errors ||
                'Failed to sync product. Please try again later.',
            variant: 'warn',
            symbolName: 'warning',
          });
        }
      },
      error: (err) => {
        this.notification.showSnackbar({
          message:
            err.error || 'Failed to sync product data. Please try again.',
          variant: 'warn',
          symbolName: 'error',
        });
      },
    });
  }

  handleDialogShow(showing: boolean) {
    if (showing) {
      this.notification.hideAlert();
    } else {
      this.showDialog.set(false);
      this.buyerForm.reset();
      this.selectedBuyer.set(undefined);
      this.dialogError.set(undefined);
    }
  }
}
